import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onChange", "onFocus", "onKeyDown", "tabIndex"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useRef, createContext, useContext } from 'react';
import i18n from 'i18n';
import { useItemList } from 'use-item-list';
import { KEY } from 'utils/ui/constants';
import { mergeCallbacks } from 'utils/ui';
import useEventListener from 'hooks/useEventListener';
import useOnClickOutside from 'hooks/useOnClickOutside';
import usePopover from 'hooks/usePopover';
import usePopoverState from 'hooks/usePopoverState';
import useUniqueId from 'hooks/useUniqueId';
import { useTabIndexContext } from 'components/TabIndexContext';
import Portal from 'components/Portal';
import Status from 'components/Status';
import VisuallyHidden from 'components/VisuallyHidden';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function defaultGetA11yStatusMessage(_ref) {
  var resultCount = _ref.resultCount,
    inputValue = _ref.inputValue;
  if (inputValue !== null && inputValue !== void 0 && inputValue.length && !resultCount) {
    return i18n.t('no_suggestions_found');
  }
  if (resultCount > 0) {
    return i18n.t('search_navigation_available_suggestions', {
      count: resultCount
    });
  }
  return '';
}
var ComboboxContext = /*#__PURE__*/createContext(null);
export function useComboboxContext() {
  var context = useContext(ComboboxContext);
  if (!context) {
    throw new Error("useComboboxContext must be used within a Combobox component's context");
  }
  return context;
}
function Combobox(_ref2) {
  var idProp = _ref2.id,
    inputValue = _ref2.inputValue,
    resultCount = _ref2.resultCount,
    onSelect = _ref2.onSelect,
    onInputChange = _ref2.onInputChange,
    _ref2$getStatusMessag = _ref2.getStatusMessage,
    getStatusMessage = _ref2$getStatusMessag === void 0 ? defaultGetA11yStatusMessage : _ref2$getStatusMessag,
    _ref2$canOpenMenu = _ref2.canOpenMenu,
    canOpenMenu = _ref2$canOpenMenu === void 0 ? true : _ref2$canOpenMenu,
    _ref2$shouldShowStatu = _ref2.shouldShowStatusMessage,
    shouldShowStatusMessage = _ref2$shouldShowStatu === void 0 ? false : _ref2$shouldShowStatu,
    _ref2$keepMenuOpenOnS = _ref2.keepMenuOpenOnSelect,
    keepMenuOpenOnSelect = _ref2$keepMenuOpenOnS === void 0 ? false : _ref2$keepMenuOpenOnS,
    children = _ref2.children;
  var inputRef = useRef(null);
  var popoverRef = useRef(null);
  var id = useUniqueId(idProp);
  var statusMessage = getStatusMessage({
    resultCount: resultCount,
    inputValue: inputValue
  });
  var tabIndexContext = useTabIndexContext();
  var popover = usePopover({
    ref: popoverRef,
    referenceRef: inputRef,
    placement: 'bottom-start',
    positionFixed: false,
    adaptivePositioning: true,
    matchReferenceWidth: true,
    arrowSize: 0
  });
  var _usePopoverState = usePopoverState(),
    isOpen = _usePopoverState.isOpen,
    open = _usePopoverState.open,
    close = _usePopoverState.close,
    toggle = _usePopoverState.toggle;
  var hasStatusMessage = shouldShowStatusMessage && Boolean(statusMessage);
  var canMenuBeOpen = Boolean(canOpenMenu && (hasStatusMessage || resultCount));
  var isMenuOpen = Boolean(canMenuBeOpen && isOpen);
  function handleSelect(selectedItem) {
    onSelect(selectedItem);
    if (keepMenuOpenOnSelect) {
      return;
    }
    close();
  }
  var itemList = useItemList({
    id: id,
    selected: inputValue,
    initialHighlightedIndex: 0,
    onSelect: handleSelect
  });
  function handleInputChange(e) {
    onInputChange(e);
    itemList.setHighlightedItem(0);
    if (e.target.value) {
      open();
    }
  }
  function handleGlobalMenuKeyEvents(event) {
    if (event.key === KEY.ESC) {
      event.preventDefault();
      close();
    }
  }
  function handleInputKeyEvents(event) {
    if (shouldShowStatusMessage) {
      return;
    }
    if (event.key === KEY.ARROW_UP) {
      event.preventDefault();
      if (isMenuOpen) {
        itemList.moveHighlightedItem(-1);
      } else {
        open();
      }
    }
    if (event.key === KEY.ARROW_DOWN) {
      event.preventDefault();
      if (isMenuOpen) {
        itemList.moveHighlightedItem(1);
      } else {
        open();
      }
    }
    if (event.key === KEY.TAB) {
      if (isMenuOpen) {
        itemList.selectHighlightedItem();
        close();
      }
    }
    if (event.key === KEY.ENTER) {
      if (isMenuOpen) {
        event.preventDefault();
        itemList.selectHighlightedItem();
        close();
      }
    }
  }

  // Handle global keyboard events when the menu is open
  useEventListener('keydown', handleGlobalMenuKeyEvents, {
    isEnabled: isMenuOpen
  });

  // Close the menu when clicking outside of the input
  useOnClickOutside([inputRef, popoverRef], close, isMenuOpen);
  var highlightedItemId = itemList.useHighlightedItemId();
  var getInputProps = function getInputProps(_ref3) {
    var onChange = _ref3.onChange,
      onFocus = _ref3.onFocus,
      onKeyDown = _ref3.onKeyDown,
      tabIndex = _ref3.tabIndex,
      otherProps = _objectWithoutProperties(_ref3, _excluded);
    return _objectSpread(_objectSpread({}, otherProps), {}, {
      id: id,
      value: inputValue,
      role: 'combobox',
      'aria-controls': itemList.listId,
      'aria-expanded': isMenuOpen ? 'true' : 'false',
      'aria-haspopup': 'true',
      'aria-activedescendant': isMenuOpen && highlightedItemId ? highlightedItemId : undefined,
      'aria-autocomplete': 'list',
      autoComplete: 'off',
      spellCheck: 'false',
      tabIndex: tabIndex || tabIndexContext,
      onKeyDown: mergeCallbacks(onKeyDown, handleInputKeyEvents),
      onChange: mergeCallbacks(onChange, handleInputChange),
      onFocus: mergeCallbacks(onFocus, function () {
        if (!isMenuOpen && canOpenMenu) {
          open();
        }
      })
    });
  };
  return /*#__PURE__*/_jsxs(ComboboxContext.Provider, {
    value: {
      popover: _objectSpread(_objectSpread({}, popover), {}, {
        isOpen: isMenuOpen,
        toggle: toggle,
        open: open,
        close: close
      }),
      getInputProps: getInputProps,
      itemList: itemList,
      statusMessage: statusMessage,
      shouldShowStatusMessage: shouldShowStatusMessage
    },
    children: [children, /*#__PURE__*/_jsx(Portal, {
      children: /*#__PURE__*/_jsx(Status, {
        as: VisuallyHidden,
        id: "".concat(id, "-a11y-hints"),
        children: isMenuOpen && statusMessage
      })
    })]
  });
}
export { ComboboxContext };
export default Combobox;